import { AfterViewInit, Component, computed, ElementRef, HostListener, ChangeDetectorRef, Inject, OnDestroy, OnInit, signal, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { FormResponse, GridActions, GridRequest, GridResponse, GridSettingsState, PendingAction, TableColumn, TextStyle } from './grid.model';
import { GridService } from '../services/grid.service';
import { LocalizePipe } from '../pipes/LocalizePipe';
import { UrlParserPipe } from '../pipes/UrlParserPipe';
import { DynamicFormatPipe } from "../pipes/DynamicFormatPipe";
import { DatePipe, DOCUMENT } from '@angular/common';
import { FilterComponent } from "./filter-component/filter-component";
import { GridSettingsModalComponent } from './grid-settings-modal-component/grid-settings-modal-component';
import { FilterItem } from './filter-component/filter-component.model';
import { SettingsModalResult } from './grid-settings-modal-component/grid-settings-modal-component.model';
import { ConfirmDialogComponent } from './confirm-dialog-component/confirm-dialog-component';
import { LoaderComponent } from "./loader-component/loader-component";
import { finalize } from 'rxjs/operators';

declare var $: any;

@Component({
  selector: 'app-grid',
  imports: [ConfirmDialogComponent, LocalizePipe, UrlParserPipe, DynamicFormatPipe, FilterComponent, FormsModule, LoaderComponent],
  templateUrl: './grid.html',
  styleUrl: './grid.css',
  providers: [DatePipe, LocalizePipe, UrlParserPipe, DynamicFormatPipe],
})
export class Grid implements AfterViewInit, OnInit, OnDestroy {
  response = signal<GridResponse>({
    grid: {
        query: '',
        authorizationRoute: '',
        extension: '',
        extensionNavigation: {
          id: '',
          columns: [],
          filters: []
        },
        columns: [],
        filters: [],
        actions: [],
        options: {
          pagesize: 0,
          ordercol: '',
          orderasc: true,
          emptymessage: '',
          export: false
        }
    },
    filter: [],
    data: [],
    id: '',
    orderasc: true,
    ordercol: '',
    page: 0,
    parameters: [],
    totalpages: 1
  });
  columnKey = signal<string>('');
  data = signal<any>([]);
  state = signal<GridRequest>({
    id: '',
    culture: 'cs-CZ',
    filter: {},
    initialization: true,
    page: 0,
    orderasc: true,
    ordercol: '',
    parameters: {}
  });
  isLoading = signal<boolean>(false);
  stateStorageName: string = '';
  responseStorageName: string = '';
  displayedColumns = signal<TableColumn[]>([]);
  displayedFilters = signal<FilterItem[]>([]);
  noDataMessage = signal<string>('');

  hasVisibleSingleAction = computed(() => {
    const actions = this.response().grid.actions;
    return actions.some(action => action.visible && action.type === 'single');
  });

  cssColumns = computed(() => this.response().grid.columns.filter(c => c.iscss === true));

  // zpracování multiple actions
  hasMultipleAction = computed(() =>
    this.response().grid.actions.some(a => a.type === "multiple")
  );
  multipleActions = computed(() =>
    this.response().grid.actions.filter(a => a.type === "multiple")
  );
  selectedKeys = signal<Set<string>>(new Set());
  selectedActionId = signal<string | null>(null);
  disabledRowClass = "disabled";
  disabledKeys = computed(() => {
    const cssCols = this.cssColumns();
    const rows = this.data();
    return rows
      .filter((row: any) => cssCols.some(attr => row[attr.name] === this.disabledRowClass))
      .map((row: any) => row[this.columnKey()]);
  });

  //action modal
  isModalVisible = signal<boolean>(false);
  pendingAction: PendingAction = { action: null, row: null};

  //ajax form
  private readonly ajaxFormEvent = 'appGridFormSubmitted';

  //culture
  private currentCulture;

  //action sticky column dle šířky tabulky
  public isTableScrollable: boolean = false;
  @ViewChild('tableContainer') tableContainer!: ElementRef<HTMLDivElement>;

  // virtuální horizontální skrollbar (position: fixed, vázaný na viewport)
  public tableWidth: number = 0;
  public showFloatingScrollbar = false;
  public floatingScrollbarLeft = 0;
  public floatingScrollbarWidth = 0;
  public floatingScrollbarTop = 0;
  private static readonly FLOATING_SCROLLBAR_HEIGHT = 16; // výška scrollbaru v grid.css
  private floatingScrollbarTicking = false;
  private isSyncingLeft = false;
  private isSyncingRight = false;
  private resizeObserver?: ResizeObserver;
  @ViewChild('tableElement') tableElement!: ElementRef<HTMLTableElement>;
  @ViewChild('fakeScrollbar') fakeScrollbar!: ElementRef<HTMLDivElement>;

  // === Šířky sloupců (automatický výpočet + ruční změna uživatelem) ===
  // maxRowLines = cílový maximální počet řádků, na které se má obsah buňky zalomit.
  public maxRowLines = 3;
  public minColumnWidth = 80;   // minimální šířka sloupce (px)
  public maxColumnWidth = 420;  // maximální šířka sloupce (px)
  public checkboxColumnWidth = 50;
  public actionColumnWidth = 138;
  private static readonly SORT_ICON_RESERVE = 28;       // rezerva na ikonu řazení v hlavičce: 24px ikona + 0.25rem margin

  // automaticky dopočítané šířky (klíč = název sloupce)
  columnWidths = signal<Record<string, number>>({});
  // šířky ručně nastavené uživatelem (mají přednost před automatickými)
  userColumnWidths = signal<Record<string, number>>({});
  // efektivní šířka sloupce = ruční přednastavení, jinak automatický výpočet
  effectiveColumnWidths = computed(() => ({
    ...this.columnWidths(),
    ...this.userColumnWidths(),
  }));
  // celková šířka tabulky (součet šířek všech viditelných sloupců + speciální sloupce)
  tableTotalWidth = computed(() => {
    const widths = this.effectiveColumnWidths();
    let sum = this.displayedColumns()
      .filter(c => c.visible)
      .reduce((acc, c) => acc + (widths[c.name] ?? this.minColumnWidth), 0);
    if (this.hasMultipleAction()) sum += this.checkboxColumnWidth;
    if (this.hasVisibleSingleAction()) sum += this.actionColumnWidth;
    return sum;
  });

  // prostor pro hlášku o prázdném gridu
  emptyColspan = computed(() => {
    let count = this.displayedColumns().filter(c => c.visible).length;
    if (this.hasMultipleAction()) count++;
    if (this.hasVisibleSingleAction()) count++;
    return Math.max(1, count);
  });

  // stav probíhajícího tažení resizeru
  resizingColumn = signal<string | null>(null);
  private resizeStartX = 0;
  private resizeStartWidth = 0;
  private measureCanvas?: HTMLCanvasElement;
  private autoWidthTicking = false;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private gridElement: ElementRef,
    private gridService: GridService,
    private dialog: MatDialog,
    private localize: LocalizePipe,
    private parseUrl: UrlParserPipe,
    private dynamicFormat: DynamicFormatPipe,
    private cdr: ChangeDetectorRef,
  ) {
    const htmlTag = this.document.documentElement;
    this.currentCulture = htmlTag.getAttribute('data-culture') || 'cs-CZ';
  }

  ngAfterViewInit() {
    this.checkScrollability();
    this.updateScrollbarWidth();
    this.updateFloatingScrollbar();
    this.observeTableResize();
    this.document.addEventListener('scroll', this.onScrollCapture, true);
  }

  // sleduje rozměr tabulky
  // po zobrazení skrytého tabu (display:none, šířka 0) tabulka získá skutečnou šířku
  // po přenačtení dat může být jiný rozměr tabulky
  private observeTableResize() {
    if (typeof ResizeObserver === 'undefined' || !this.tableElement) return;
    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(() => {
        this.checkScrollability();
        this.updateScrollbarWidth();
        this.updateFloatingScrollbar();
      });
    });
    this.resizeObserver.observe(this.tableElement.nativeElement);
  }

  // scroll (v capture fázi zachytí i scroll vnořených kontejnerů) -> přepočet pozice fixed skrollbaru
  private onScrollCapture = () => {
    if (this.floatingScrollbarTicking) return;
    this.floatingScrollbarTicking = true;
    requestAnimationFrame(() => {
      this.floatingScrollbarTicking = false;
      this.updateFloatingScrollbar();
    });
  };

  ngOnInit(): void {
    this.noDataMessage.set(this.localize.transform('sdg.grid.nacitani_dat'));
    const gridID = this.gridElement.nativeElement.getAttribute('data-custom-grid-id');
    const paramAttrPrefix = "data-custom-grid-param-";
    const stateParams = Array.from(this.gridElement.nativeElement.attributes)
      .filter((attr: any) => attr.name.startsWith(paramAttrPrefix))
      .reduce<Record<string, any>>((acc: any, attr: any) => {
        const key = attr.name.substring(paramAttrPrefix.length);
        acc[key] = attr.value;
        return acc;
      }, {});

    this.state.update(state => ({
      ...state,
      id: gridID,
      culture: this.currentCulture,
      parameters: stateParams
    })); 
    const paramValues = Object.values(stateParams).join(',')
    this.stateStorageName = `grid_state_${gridID}${paramValues}`;
    this.responseStorageName = `grid_response_${gridID}${paramValues}`;
    this.response.update(response =>({
      ...response,
      id: gridID
    }));
    this.restoreResponse();
    this.restoreState();
    this.applyPersistedPageSize();
    this.applyPersistedColumnWidths();
    this.getData();

    // listener pro custom.forms.close - pro prenacteni dat
    $(window).on(this.ajaxFormEvent, this.handleJQueryEvent);
  }

  private applyPersistedPageSize(): void {
    const settings = this.gridService.getSettings(this.state().id);
    if (settings?.pageSize) {
      const sanitized = this.sanitizePageSize(settings.pageSize);
      if (sanitized) {
        this.state.update(state => ({ ...state, pagesize: sanitized }));
      }
    }
  }

  private sanitizePageSize(value: number | undefined | null): number | undefined {
    if (value === null || value === undefined) return undefined;
    const n = Number(value);
    return Number.isFinite(n) && n > 0 ? Math.floor(n) : undefined;
  }

  private applyPersistedColumnWidths(): void {
    const settings = this.gridService.getSettings(this.state().id);
    if (settings?.columnWidths) {
      const sanitized: Record<string, number> = {};
      Object.entries(settings.columnWidths).forEach(([name, value]) => {
        const n = Number(value);
        if (Number.isFinite(n) && n > 0) sanitized[name] = Math.round(n);
      });
      this.userColumnWidths.set(sanitized);
    }
  }

  ngOnDestroy() {
    $(window).off(this.ajaxFormEvent, this.handleJQueryEvent);
    this.resizeObserver?.disconnect();
    this.document.removeEventListener('scroll', this.onScrollCapture, true);
    this.document.removeEventListener('mousemove', this.onColumnResizeMove);
    this.document.removeEventListener('mouseup', this.onColumnResizeEnd);
  }

  onSort(column: TableColumn): void {
    if (!column.sortable) return;

    let orderAsc = this.state().orderasc;
    if (this.state().ordercol == column.name){
      orderAsc = !orderAsc;
    }
    else {
      orderAsc = true;
    }

    this.state.update(state => ({
      ...state,
      ordercol: column.name,
      orderasc: orderAsc
    }));
    this.getData();
  }

  onPageChange(newPage: number){
    if (newPage === this.response().page || newPage < 0 || newPage >= this.response().totalpages){
      return;
    }
    this.state.update(state => ({
      ...state,
      page: newPage
    }));
    this.getData();
  }

  onFilterData(filters: any) {
    this.state.update(state => ({
      ...state,
      filter: filters,
      page: 0
    }));
    this.syncFilterToUrl(filters);
    this.getData();
  }

  // Aplikuje filtr z URL (napr. ?ProgramKod=BARRANDE) az po nacteni definice gridu.
  // URL cteme primo z prohlizece - Angular Router v rezimu Web Component (Angular Elements)
  // neprovadi initial navigation, takze ActivatedRoute.snapshot.queryParams je prazdne.
  // Vraci true, pokud byl odvozen novy filtr a spustilo se nove nacteni dat.
  private applyUrlFilter(): boolean {
    const params = new URLSearchParams(window.location.search);
    if (Array.from(params.keys()).length === 0) return false;

    const allFilters = [
      ...this.response().grid.filters,
      ...this.response().grid.extensionNavigation.filters,
    ];
    if (allFilters.length === 0) return false;

    const urlFilter: Record<string, any> = {};
    let hasAny = false;
    allFilters.forEach(f => {
      const raw = params.get(f.column);
      if (raw == null || raw === '') return;
      urlFilter[f.column] = f.type === 'multiselect'
        ? params.getAll(f.column)
        : raw;
      hasAny = true;
    });
    if (!hasAny) return false;

    // pokud uz je stejny filtr aktivni (napr. obnoveny ze sessionStorage), dotaz neopakujeme
    if (JSON.stringify(this.state().filter) === JSON.stringify(urlFilter)) return false;

    this.state.update(state => ({ ...state, filter: urlFilter, page: 0 }));
    this.getData();
    return true;
  }

  private syncFilterToUrl(filters: any): void {
    const allFilters = [
      ...this.response().grid.filters,
      ...this.response().grid.extensionNavigation.filters,
    ];
    const knownCols = new Set(allFilters.map(f => f.column));
    const next: Record<string, any> = {};
    knownCols.forEach(c => { next[c] = null; });
    Object.entries(filters).forEach(([k, v]) => {
      if (!knownCols.has(k)) return;
      if (v === null || v === undefined || v === '' || (Array.isArray(v) && v.length === 0)) {
        next[k] = null;
      } else {
        next[k] = v;
      }
    });
 
    const searchParams = new URLSearchParams();
    Object.entries(next).forEach(([key, value]) => {
      if (value === null || value === undefined || value === ''|| (Array.isArray(value) && value.length === 0)) {
        return;
      } else if (Array.isArray(value)) {
        value.forEach(v => searchParams.append(key, v));
      } else {
        searchParams.set(key, value);
      }
    });

    // sestaveni url
    const newUrl = `${window.location.pathname}?${searchParams.toString()}${window.location.hash}`;
    window.history.replaceState(window.history.state, '', newUrl);
  }

  private getData(): void {
    this.saveState();
    this.isLoading.set(true);
    this.gridService.getData(this.state())
      .pipe(
        finalize(() => {
          this.isLoading.set(false);
        })
      )
      .subscribe({
        next: (response) => {
          if (this.state().initialization) {
            this.response.set(response);
            this.data.set(response.data);
            this.state.update(state => ({
              ...state,
              initialization: false,
              ordercol: response.ordercol,
              orderasc: response.orderasc,
              pagesize: state.pagesize ?? this.sanitizePageSize(response.grid?.options?.pagesize)
            }));
            this.saveState();
          }
          else {
            this.data.set(response.data);

            const tempResponse = {
              ...this.response(),
              page: response.page,
              grid: response.grid,
              totalpages: response.totalpages
            }

            this.response.set(tempResponse);
          }

          // Provedení filtrace z url parametrů se provede až po načtení definice gridu
          // Hodnota pro multiselect musí být vložena jako pole
          if (this.applyUrlFilter())
          {
            return;
          }

          this.setColumnKey()

          if (this.displayedColumns.length === 0){
            this.initializeGrid();
          }

          this.saveResponse();
          this.scheduleAutoColumnWidths();

          if (!response.data?.length){
            this.noDataMessage.set(this.localize.transform('sdg.grid.no-data'));
          }
        },
        error: (err) => {
          this.data.set(undefined);
          this.noDataMessage.set(this.localize.transform('sdg.grid.nacitani_dat_selhalo'))
          console.log('Failed to retrieve grid. Error: ' + err);
        }
      });
  }

  openSettings() {
    const dialogRef = this.dialog.open(GridSettingsModalComponent, {
      width: '800px',
      maxWidth: '85vw',
      height: '92%',
      data: {
        allColls: [...this.response().grid.columns.filter(col => col.visible), ...this.response().grid.extensionNavigation.columns.filter(col => col.visible)],
        allFilters: [...this.response().grid.filters, ...this.response().grid.extensionNavigation.filters],
        grid: this.response().grid,
        settingsKey: this.state().id,
      }
    });

    dialogRef.afterClosed().subscribe((result: SettingsModalResult | undefined) => {
    if (result) {
      this.displayedColumns.set(result.visibleColumns);
      this.displayedFilters.set(result.visibleFilters);
      this.scheduleAutoColumnWidths();
      const sanitized = this.sanitizePageSize(result.pageSize);
      if (sanitized && sanitized !== this.state().pagesize) {
        this.state.update(state => ({ ...state, pagesize: sanitized, page: 0 }));
        this.getData();
      }
    }
  });
  }

  private initializeGrid() {
    const allCols = [...this.response().grid.columns, ...this.response().grid.extensionNavigation.columns];
    const allFilters = [...this.response().grid.filters, ...this.response().grid.extensionNavigation.filters];
    const settings = this.gridService.getSettings(this.state().id);
    if (settings) {
      const orderedCols: TableColumn[] = [];
      settings.visibleColumns.forEach((name: string) =>{
        const col = allCols.find(c => c.name === name);
        if (col) orderedCols.push(col);
      });

      const orderedFilters: FilterItem[] = [];
      settings.visibleFilters.forEach((column: string) => {
        const filter = allFilters.find(f => f.column === column);
        if (filter) orderedFilters.push(filter);
      });

      this.displayedColumns.set(orderedCols);
      this.displayedFilters.set(orderedFilters);

      const sanitized = this.sanitizePageSize(settings.pageSize);
      if (sanitized && sanitized !== this.state().pagesize) {
        this.state.update(state => ({ ...state, pagesize: sanitized }));
      }
    }
    else {
      this.displayedColumns.set([...this.response().grid.columns]);
      this.displayedFilters.set([...this.response().grid.filters]);
    }
  }

  private saveState() {
    // parametry se nekesuji
    const { parameters, ...dataToSave } = this.state();
    this.saveToStorage(this.stateStorageName, dataToSave);
  }

  private saveResponse() {
    // data a grid actions se v gridu nekesuji
    const { 
      data,
      grid,
      ...restOfResponse
    } = this.response();
    const responseToSave = {
      ...restOfResponse
    };

    this.saveToStorage(this.responseStorageName, responseToSave);
  }

  private saveToStorage(storageKey: string, itemToSave: any) {
    sessionStorage.setItem(storageKey, JSON.stringify(itemToSave));
  }

  private restoreState() {
    const storedState = this.restoreFromStorage(this.stateStorageName, this.state());
    const tempState = {
      ...storedState,
      culture: this.state().culture,
      parameters: this.state().parameters
    }
    this.state.set(tempState);
  }

  private restoreResponse() {
    const storedResponse = this.restoreFromStorage(this.responseStorageName, this.response());
    const tempResponse = {
      ...storedResponse,
      data: this.response().data,
      grid: this.response().grid
    }

    this.response.set(tempResponse);
    this.setColumnKey();
  }

  private restoreFromStorage(storageKey: string, itemToRestore: any): any {
    const savedItem = sessionStorage.getItem(storageKey);

    if (savedItem) {
      try {
        return JSON.parse(savedItem);
      } catch (e) {
        this.removeAllFromStorage();
        this.state.update(state =>({
          ...state,
          initialization: true
        }));
      }
    }
    return itemToRestore;
  }

  private removeAllFromStorage(){
    sessionStorage.removeItem(this.stateStorageName);
    sessionStorage.removeItem(this.responseStorageName);
  }

  private setColumnKey() {
    let key = this.response().grid.columns.find(c => c.key === true)?.name;
    if (key) {
      this.columnKey.set(key);
    }
  }

  get visiblePages(): number[] {
    const total = this.response().totalpages;
    const page = this.response().page;
    const pages = [];
    for (let i = 0; i < total; i++) {
        //zobrazujeme max 3 stranky vcetne finalni - pokud je jich vic, vlozime pred posledni tecky
        // zpracovame pro krajni cisla a +- 2 od soucasne hodnoty
        // druhy radek - pokud jsme jeste na krajnich hodnotach, zobrazujeme vice cisel druhe strany
        if (i == 0 || i == total - 1 || (page - 2 == i) || (page - 1 == i) || (page == i) || (page + 1 == i) || (page + 2 == i)
            || (page <= 3 && i <= 6) || (page >= (total - 4) && i >= (total - 7))) {
          pages.push(i);
        }
        else if (1 == i || i == (total - 2)) {
          //zaporne cislo je ve view nahrazeno trema teckama
          pages.push(-i);
        }
    }
    return pages;
  }

  // zpracovani multiple actions
  toggleRow(id: string, checked: boolean) {
    if (this.disabledKeys().includes(id)) {
      return;
    }

    this.selectedKeys.update(currentSet => {
      const newSet = new Set(currentSet);
      if (checked) {
        newSet.add(id);
      } else {
        newSet.delete(id);
      }
      return newSet;
    });
  }

  toggleAll(checked: boolean) {
    if (!this.data()?.length) {
      return;
    }
    
    if (checked) {
      const allKeys = this.data()
        .filter((r:any) => !this.disabledKeys().includes(r[this.columnKey()]))
        .map((r: any) => r[this.columnKey()]);
      this.selectedKeys.set(new Set(allKeys));
    } else {
      this.selectedKeys.set(new Set());
    }
  }

  executeMultipleAction() {
    const actionId = this.selectedActionId();
    const action = this.multipleActions().find(a => a.displayname === actionId);
    const keysToSend = Array.from(this.selectedKeys());

    if (!action || keysToSend.length === 0) return;
    this.isLoading.set(true);
    this.gridService.sendAction(action.method, action.url, keysToSend)
      .pipe(
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (data) => {
          this.processActionResponse(data);
        },
        error: (err) => {
          window.custom?.utils?.toast(this.localize.transform('sdg.grid.error', err.error.message), 'warning', 5000);
        }
      });
  }

  // pomocná metoda pro checkbox v hlavičce
  isAllSelected = computed(() => {
    const rows = this.data();
    if (!rows?.length) {
      return false;
    }
    return rows.length > 0 && rows.every((r: any) => this.selectedKeys().has(r[this.columnKey()]));
  });
  
  // pomocná metoda pro 
  hasSelection = computed(() => this.selectedKeys().size > 0);

  isActionSelected = computed(() => {
    const actionId = this.selectedActionId();
    if (actionId === null || actionId === undefined) {
      return false;
    }
    // kontrola prázdného stringu
    // (!!value převede string na boolean: '' -> false, 'abc' -> true)
    return !!actionId.toString().trim();
  });

  // funkce pro zobrazení ajax modálu nebo ajax funkce
  onAjaxActionClick(action: GridActions, row: any, event: Event): void {
    event.preventDefault();

    if (!action.confirmation){
      const parsedUrl = this.parseUrl.transform(action.url, row, this.state().parameters);
      this.isLoading.set(true);
      if (action.urltarget === "download"){
        this.gridService.sendDownloadAction(action.method, parsedUrl)
          .pipe(
            finalize(() => this.isLoading.set(false))
          )
          .subscribe({
            next: (response) => {
              this.responseToDownload(response);
            },
            error: (err) => {
              window.custom?.utils?.toast(this.localize.transform('sdg.grid.error', err.error.message), 'warning', 5000);
            }
          });
      }
      else {
        this.gridService.sendAction(action.method, parsedUrl, {})
          .pipe(
            finalize(() => this.isLoading.set(false))
          )
          .subscribe({
            next: (data) => {
              this.processActionResponse(data);
            },
            error: (err) => {
              window.custom?.utils?.toast(this.localize.transform('sdg.grid.error', err.error.message), 'warning', 5000);
            }
          });
      }
    }
    else {
      this.pendingAction = { action: action, row: row};
      this.isModalVisible.set(true);
    }
  }

  private responseToDownload(response: any) {
    const data = response.body as Blob;

    if (data) {
      //default name
      let filename = 'download_file';
      // zip
      if (data.type === 'application/zip') {
        filename = 'download.zip'
      }
      // xlsx
      else if (data.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet') {
        filename = 'export.xlsx';
      }

      const contentDisposition = response.headers.get('Content-Disposition');
      
      if (contentDisposition) {
          const filenameRegex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/;
          const matches = filenameRegex.exec(contentDisposition);
          if (matches != null && matches[1]) {
              filename = matches[1].replace(/['"]/g, '');
              filename = decodeURIComponent(filename); 
          }
      }

      const url = window.URL.createObjectURL(response.body);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();

      // Odstranění dočasného elementu
      document.body.removeChild(a);
      setTimeout(() => {
        window.URL.revokeObjectURL(url);
      }, 150);
    }
  }

  private processActionResponse(data: any) {
      if (data && data.type === "$FormsResponse")
      {
        this.processFormsResponse(data);
      }
      else if (data.trim().startsWith("<form")){
        this.openForm(data);
      }
      else {
        window.custom?.utils?.toast(this.localize.transform('sdg.grid.action.completed'), 'success', 3000);
        this.selectedKeys.set(new Set());
        this.selectedActionId.set(null);
        // prenacteni gridu
        this.getData(); 
      }
  }

  private openForm(data: string) {
      const formsOpened = window.custom?.forms?.opened;
      if (typeof formsOpened === 'function') {
        formsOpened(data);
      }
  }

  private processFormsResponse(data: FormResponse) {
    if (data.refresh) {
      if (data.message) {
        sessionStorage.setItem('custom.message', JSON.stringify(data.message));
      }
      location.reload();
    }
    else if (data.redirectUrl) {
      if (data.message) {
        sessionStorage.setItem('custom.message', JSON.stringify(data.message));
      }
      window.location.href = data.redirectUrl;
    }
    else {
      if (data.message) {
        const level = data.message.level === 0 ? 'success' : 'warning';
        window.custom?.utils?.toast(data.message.message, level, 3000);
      }
      this.selectedKeys.set(new Set());
      this.selectedActionId.set(null);
      this.getData();
    }
  }

  onExportActionClick(event: Event){
    event.preventDefault();
    this.isLoading.set(true);
    
    this.gridService.sendDownloadAction(undefined, undefined, this.state())
      .pipe(
        finalize(() => this.isLoading.set(false))
      )
      .subscribe({
        next: (response) => {
          this.responseToDownload(response);
        },
        error: (err) => {
          window.custom?.utils?.toast(this.localize.transform('sdg.grid.error', err.error.message), 'warning', 5000);
        }
      });
  }

  handleAjaxModalResult(result: boolean): void {
    this.isModalVisible.set(false);

    if (result === true && this.pendingAction) {
      const parsedUrl = this.parseUrl.transform(this.pendingAction.action.url, this.pendingAction.row, this.state().parameters)
      this.isLoading.set(true);
      this.gridService.sendAction(this.pendingAction.action.method, parsedUrl, {})
        .pipe(
          finalize(() => this.isLoading.set(false))
        )
        .subscribe({
          next: (response) => {
            this.getData();
          },
          error: (err) => {
            console.error('Chyba', err);
            window.custom?.utils?.toast(this.localize.transform('sdg.grid.error', err.error.message), 'warning', 5000);
          }
        });
    }

    this.pendingAction = { action: null, row: null};
  }

  // prenacteni dat po odeslani ajax formuláře
  private handleJQueryEvent = (event: any, externalData: any) => {
    this.getData();
  };

  // ziskani css trid ze sloupcu
  public getCssClassesFromColumns(row: any): string {
    let result = this.cssColumns()
    .map(col => row[col.name])
    .filter(c => c)
    .join(' ');
    return result;
  }

  public rowIsDisabled(rowKey: any): boolean {
    return this.disabledKeys().includes(rowKey);
  }

  // spusti globalni js funkci definovanou v gridu (action.jsfunction), preda ji aktualni radek a parametry gridu
  onJsActionClick(action: any, row: any, event: Event): void {
    event.preventDefault();
    const fn = this.resolveGlobalFunction(action.jsfunction);
    if (typeof fn === 'function') {
      fn(row, this.state().parameters, () => this.getData());
    } else {
      console.error('Grid action: js funkce nenalezena - ' + action.jsfunction);
    }
  }

  // dohleda funkci podle teckove notace (napr. "custom.dokument.nahratZadosti") na window objektu
  private resolveGlobalFunction(path: string): any {
    if (!path) {
      return undefined;
    }
    return path.split('.').reduce((acc: any, part: string) => (acc ? acc[part] : undefined), window as any);
  }

  @HostListener('window:resize')
  onResize() {
    // změnila se šířka okna -> přepočítat šířky sloupců podle nové šířky kontejneru
    this.scheduleAutoColumnWidths();
  }

  // Vypočítá viditelnost a pozici plovoucího horizontálního skrollbaru.
  // Používáme position: fixed (vázáno na viewport), aby fungoval i když je grid
  // krátký a odsunutý pod přehyb - na rozdíl od position: sticky, který je
  // omezen na containing block (box <app-grid>).
  private updateFloatingScrollbar() {
    if (!this.tableContainer || !this.tableElement) return;
    const container = this.tableContainer.nativeElement;
    const h = Grid.FLOATING_SCROLLBAR_HEIGHT;

    // přetéká tabulka horizontálně?
    const overflowX = this.tableElement.nativeElement.scrollWidth > container.clientWidth + 1;
    const rect = container.getBoundingClientRect();
    const viewportH = window.innerHeight;
    // je oblast tabulky (aspoň na výšku skrollbaru) ve viewportu?
    const inView = rect.top < viewportH && rect.bottom > h;

    this.showFloatingScrollbar = overflowX && inView;
    if (this.showFloatingScrollbar) {
      this.floatingScrollbarLeft = rect.left;
      this.floatingScrollbarWidth = container.clientWidth;
      // připnutí ke spodku okna
      this.floatingScrollbarTop = Math.min(viewportH - h, rect.bottom - h);
    }
    this.cdr.detectChanges();
  }

  private checkScrollability() {
    if (this.tableContainer) {
      const el = this.tableContainer.nativeElement;
      // zda je fyzická šířka obsahu větší než viditelná šířka kontejneru
      this.isTableScrollable = el.scrollWidth > el.clientWidth;
      this.cdr.detectChanges(); 
    }
  }

  private updateScrollbarWidth() {
    // předání šířky tabulky vnořenému divu scrollbaru
    if (this.tableElement) {
      this.tableWidth = this.tableElement.nativeElement.scrollWidth;
      this.cdr.detectChanges();
    }
  }

  public syncScroll(scrolledElement: HTMLDivElement, targetElement: HTMLDivElement) {
    if (this.isSyncingLeft || this.isSyncingRight) return;

    if (scrolledElement === this.tableContainer.nativeElement) {
      this.isSyncingLeft = true;
      targetElement.scrollLeft = scrolledElement.scrollLeft;
      setTimeout(() => this.isSyncingLeft = false, 10);
    } else {
      this.isSyncingRight = true;
      targetElement.scrollLeft = scrolledElement.scrollLeft;
      setTimeout(() => this.isSyncingRight = false, 10);
    }
  }

  // === Automatický výpočet šířky sloupců ===

  // přepočet až po vykreslení
  // opakovaná volání (např. při tažení okraje okna) se sloučí do jednoho přepočtu na snímek
  private scheduleAutoColumnWidths() {
    if (this.autoWidthTicking) return;
    this.autoWidthTicking = true;
    requestAnimationFrame(() => {
      this.autoWidthTicking = false;
      this.computeAutoColumnWidths();
    });
  }

  // Dopočítá šířky sloupců:
  //  1) sloupec nejprve dostane šířku, aby se obsah vešel na JEDEN řádek,
  //     nejvýše však maxColumnWidth,
  //  2) pokud ani na maxColumnWidth obsah nevejde do ~maxRowLines řádků, šířka se
  //     zvedne NAD maxColumnWidth tak, aby se vešel do stanoveného počtu řádků,
  //  3) šířka nikdy neklesne pod šířku hlavičky ani pod minColumnWidth.
  // Sloupec se nezužuje pod jeden řádek jen proto, aby se tabulka vešla do viewportu
  // Když je naopak místo, sloupce se rovnoměrně roztáhnou (max do maxColumnWidth).
  // Ručně nastavené šířky (userColumnWidths) mají přes effectiveColumnWidths přednost.
  private computeAutoColumnWidths() {
    const cols = this.displayedColumns().filter(c => c.visible);
    if (!cols.length || !this.tableElement || !this.tableContainer) return;

    const rows = this.data() ?? [];
    const tdEl = this.tableElement.nativeElement.querySelector('tbody td');
    const thEl = this.tableElement.nativeElement.querySelector('thead th');
    // zjištění fontu
    const triggerEl = this.tableElement.nativeElement.querySelector('thead th .gov-sortable-table__trigger') ?? thEl;
    const bodyFont = this.getFont(tdEl, '400 1rem sans-serif', "0.1875px");
    const headFont = this.getFont(triggerEl, '700 1rem sans-serif', "0.375px");
    const bodyPad = this.getHorizontalPadding(tdEl);
    const headPad = this.getHorizontalPadding(thEl);

    const lines = Math.max(1, this.maxRowLines);

    const desiredWidths: number[] = [];

    for (const col of cols) {
      // šipka řazení
      const iconReserve = col.sortable ? Grid.SORT_ICON_RESERVE : 0;
      const headerNeeded =
        this.measureText(this.localize.transform(col.displayname), headFont, true) + iconReserve + headPad + 2;

      // nejdelší obsah buňky na jednom řádku
      let longestCell = 0;
      for (const row of rows) {
        const w = this.measureText(this.cellTextForMeasure(row[col.name], col), bodyFont) + 2;
        if (w > longestCell) longestCell = w;
      }

      // 1) šířka pro obsah na jeden řádek, nejvýš maxColumnWidth
      const oneLineCapped = Math.min(this.maxColumnWidth, longestCell + bodyPad);
      // 2) šířka, aby se obsah vešel do ~lines řádků (bez omezení maxColumnWidth) -
      const neededForLines = longestCell / lines + bodyPad;
      // výsledná šířka
      const width = Math.max(this.minColumnWidth, headerNeeded, oneLineCapped, neededForLines);
      desiredWidths.push(width);
    }

    // dostupná šířka pro sloupce (bez speciálních sloupců); malá rezerva
    // proti vzniku 1px horizontálního scrollbaru ze zaokrouhlení
    let reserved = 0;
    if (this.hasMultipleAction()) reserved += this.checkboxColumnWidth;
    if (this.hasVisibleSingleAction()) reserved += this.actionColumnWidth;
    const availForData = Math.max(0, this.tableContainer.nativeElement.clientWidth - reserved - 2);

    const desiredSum = desiredWidths.reduce((a, b) => a + b, 0);
    // je-li místo, sloupce rovnoměrně roztáhneme (max do maxColumnWidth); jinak
    // ponecháme potřebné šířky a necháme horizontální scroll (nezužujeme pod jeden řádek)
    const finalWidths = desiredSum <= availForData ? this.growToFill(desiredWidths, availForData) : desiredWidths;

    const widths: Record<string, number> = {};
    cols.forEach((col, i) => widths[col.name] = Math.round(finalWidths[i]));
    this.columnWidths.set(widths);

    // šířka tabulky se změnila -> aktualizace skrollbarů
    requestAnimationFrame(() => {
      this.checkScrollability();
      this.updateScrollbarWidth();
      this.updateFloatingScrollbar();
    });
  }

  // Rovnoměrně roztáhne sloupce, aby vyplnily cílovou šířku, každý max do maxColumnWidth.
  // Když jsou všechny na maximu a stále je místo, vrátí je nedotažené (tabulka zůstane užší).
  private growToFill(natural: number[], target: number): number[] {
    const widths = [...natural];
    const capped = new Array(widths.length).fill(false);
    for (let guard = 0; guard < 100; guard++) {
      const extra = target - widths.reduce((a, b) => a + b, 0);
      if (extra <= 0.5) break;
      const growable = widths
        .map((w, i) => (!capped[i] && w < this.maxColumnWidth ? i : -1))
        .filter(i => i >= 0);
      if (!growable.length) break;
      const share = extra / growable.length;
      let changed = false;
      for (const i of growable) {
        const next = Math.min(this.maxColumnWidth, widths[i] + share);
        if (next > widths[i]) { widths[i] = next; changed = true; }
        if (widths[i] >= this.maxColumnWidth) capped[i] = true;
      }
      if (!changed) break;
    }
    return widths;
  }

  private getHorizontalPadding(el: Element | null): number {
    if (!el) return 16;
    const cs = getComputedStyle(el);
    return (parseFloat(cs.paddingLeft) || 0) + (parseFloat(cs.paddingRight) || 0);
  }

  // převede hodnotu buňky na text stejně, jako ji zobrazuje šablona (dynamicFormat)
  private cellTextForMeasure(value: any, col: TableColumn): string {
    const formatted = this.dynamicFormat.transform(value, col);
    return formatted == null ? '' : String(formatted);
  }

  private measureText(text: string, style: TextStyle, isUppercase: boolean = false): number {
    if (!this.measureCanvas) {
      this.measureCanvas = this.document.createElement('canvas');
    }
    const ctx = this.measureCanvas.getContext('2d');
    if (!ctx) return 0;
    ctx.letterSpacing = style.letterSpacing;
    let textToMeasure = text;
    ctx.font = style.font;
    if (isUppercase)
    {
      textToMeasure = text.toUpperCase();
    }
    return ctx.measureText(textToMeasure ?? '').width;
  }

  private getFont(el: Element | null, defaultFont: string, defaultLS: string): TextStyle {
    if (!el) return {
      font: defaultFont,
      letterSpacing: defaultLS
    };
    const cs = getComputedStyle(el);
    return {
        font: `${cs.fontWeight} ${cs.fontSize} ${cs.fontFamily}`,
        letterSpacing: cs.letterSpacing
    }
  }

  // ruční změna šířky sloupce
  onColumnResizeStart(event: MouseEvent, col: TableColumn) {
    // zabráníme řazení a označování textu při tažení
    event.preventDefault();
    event.stopPropagation();

    this.resizingColumn.set(col.name);
    this.resizeStartX = event.pageX;
    this.resizeStartWidth = this.effectiveColumnWidths()[col.name] ?? this.minColumnWidth;

    this.document.addEventListener('mousemove', this.onColumnResizeMove);
    this.document.addEventListener('mouseup', this.onColumnResizeEnd);
    this.document.body.style.userSelect = 'none';
    this.document.body.style.cursor = 'col-resize';
  }

  private onColumnResizeMove = (event: MouseEvent) => {
    const columnName = this.resizingColumn();
    if (!columnName) return;
    const delta = event.pageX - this.resizeStartX;
    const newWidth = Math.max(this.minColumnWidth, Math.round(this.resizeStartWidth + delta));
    this.userColumnWidths.update(widths => ({ ...widths, [columnName]: newWidth }));
  };

  private onColumnResizeEnd = () => {
    if (!this.resizingColumn()) return;
    this.resizingColumn.set(null);
    this.document.removeEventListener('mousemove', this.onColumnResizeMove);
    this.document.removeEventListener('mouseup', this.onColumnResizeEnd);
    this.document.body.style.userSelect = '';
    this.document.body.style.cursor = '';

    this.persistColumnWidths();
    this.checkScrollability();
    this.updateScrollbarWidth();
    this.updateFloatingScrollbar();
  };

  // uloží ruční šířky do nastavení gridu (localStorage), ostatní nastavení zachová
  private persistColumnWidths() {
    const id = this.state().id;
    const existing = this.gridService.getSettings(id);
    const base: GridSettingsState = existing ?? {
      visibleColumns: this.displayedColumns().map(c => c.name),
      visibleFilters: this.displayedFilters().map(f => f.column),
    };
    this.gridService.saveSettings(id, { ...base, columnWidths: this.userColumnWidths() });
  }
}
